import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trophy, Users, MessageSquare, User } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import DashboardHeader from '../components/DashboardHeader';
import NavigationCarousel from '../components/NavigationCarousel';
import SearchBar from '../components/SearchBar';

const Challenges = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('local');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [showChallengeModal, setShowChallengeModal] = useState(false);

  const handleSearch = (searchTerm) => {
    console.log('Search Local, International, University Challenges:', searchTerm);
    // In real app, this would filter challenges
  };

  const handleChallengeClick = (challenge) => {
    setSelectedChallenge(challenge);
    setShowChallengeModal(true);
  };

  const handleStartChallenge = (challenge) => {
    alert(`Starting ${challenge.title}!\n\nYou'll now have access to:\n• Challenge briefing and requirements\n• Design tools and resources\n• Collaboration workspace\n• Submission portal\n• Progress tracking`);
    setShowChallengeModal(false);
  };

  const handleJoinChallenge = (challenge) => {
    alert(`Joined ${challenge.title}!\n\nYou can now:\n• View full challenge details\n• Access team collaboration tools\n• Submit your designs\n• Track progress and deadlines`);
    setShowChallengeModal(false);
  };

  const tabs = [
    { id: 'local', label: 'LOCAL' },
    { id: 'university', label: 'UNIVERSITY' },
    { id: 'international', label: 'INTERNATIONAL' },
  ];

  const challenges = {
    local: [
      {
        title: 'Green Tech Park Design',
        subtitle: 'Design a sustainable technology park for the local community',
        category: 'Urban Sustainability',
        difficulty: 'Intermediate',
        duration: '2 weeks',
        participants: 24,
        reward: 500,
        status: 'active',
        progress: 45,
        deadline: '5 days left',
        featured: true
      },
      {
        title: 'Smart City Infrastructure',
        subtitle: 'Develop intelligent infrastructure solutions',
        category: 'Technology',
        difficulty: 'Advanced',
        duration: '3 weeks',
        participants: 18,
        reward: 750,
        status: 'active',
        progress: 0,
        deadline: '2 weeks left',
        featured: false
      },
      {
        title: 'Community Center Renovation',
        subtitle: 'Redesign local community spaces',
        category: 'Architecture',
        difficulty: 'Beginner',
        duration: '1 week',
        participants: 35,
        reward: 250,
        status: 'completed',
        progress: 100,
        deadline: 'Completed',
        featured: false
      },
      {
        title: 'Structural Engineering Analysis',
        subtitle: 'Analyze and design load-bearing structures for high-rise buildings',
        category: 'Engineering',
        difficulty: 'Advanced',
        duration: '3 weeks',
        participants: 28,
        reward: 750,
        status: 'active',
        progress: 15,
        deadline: '2 weeks left',
        featured: false
      },
      {
        title: 'Sustainable Architecture Design',
        subtitle: 'Create eco-friendly residential buildings with green technology',
        category: 'Architecture',
        difficulty: 'Intermediate',
        duration: '2 weeks',
        participants: 35,
        reward: 500,
        status: 'active',
        progress: 40,
        deadline: '1 week left',
        featured: false
      },
      {
        title: 'Civil Engineering Project',
        subtitle: 'Design and plan major infrastructure development',
        category: 'Engineering',
        difficulty: 'Advanced',
        duration: '4 weeks',
        participants: 22,
        reward: 850,
        status: 'upcoming',
        progress: 0,
        deadline: '3 days left',
        featured: false
      },
      {
        title: 'Interior Design Innovation',
        subtitle: 'Redesign commercial spaces with modern aesthetic principles',
        category: 'Interior Design',
        difficulty: 'Beginner',
        duration: '1 week',
        participants: 42,
        reward: 300,
        status: 'completed',
        progress: 100,
        deadline: 'Completed',
        featured: false
      },
      {
        title: 'Mechanical Systems Design',
        subtitle: 'Design HVAC and mechanical systems for large buildings',
        category: 'Engineering',
        difficulty: 'Intermediate',
        duration: '3 weeks',
        participants: 26,
        reward: 600,
        status: 'active',
        progress: 25,
        deadline: '2 weeks left',
        featured: false
      }
    ],
    international: [
      {
        title: 'Global Climate Solutions',
        subtitle: 'Address climate change through innovative design',
        category: 'Environmental',
        location: 'Copenhagen, Denmark',
        difficulty: 'Advanced',
        duration: '4 weeks',
        participants: 156,
        reward: 1000,
        status: 'active',
        progress: 20,
        deadline: '3 weeks left',
        featured: true
      },
      {
        title: 'Cross-Cultural Housing',
        subtitle: 'Design housing that respects cultural diversity',
        category: 'Architecture',
        location: 'Singapore, Singapore',
        difficulty: 'Intermediate',
        duration: '2 weeks',
        participants: 89,
        reward: 600,
        status: 'active',
        progress: 60,
        deadline: '1 week left',
        featured: false
      },
      {
        title: 'International Bridge Design',
        subtitle: 'Design innovative bridge structures connecting countries',
        category: 'Engineering',
        location: 'Istanbul, Turkey',
        difficulty: 'Advanced',
        duration: '5 weeks',
        participants: 89,
        reward: 1100,
        status: 'active',
        progress: 30,
        deadline: '4 weeks left',
        featured: false
      },
      {
        title: 'Cultural Heritage Architecture',
        subtitle: 'Preserve and modernize historic architectural landmarks',
        category: 'Architecture',
        location: 'Rome, Italy',
        difficulty: 'Intermediate',
        duration: '3 weeks',
        participants: 67,
        reward: 650,
        status: 'active',
        progress: 50,
        deadline: '2 weeks left',
        featured: false
      },
      {
        title: 'Environmental Engineering Solutions',
        subtitle: 'Develop sustainable waste management and water treatment systems',
        category: 'Engineering',
        location: 'Amsterdam, Netherlands',
        difficulty: 'Advanced',
        duration: '4 weeks',
        participants: 78,
        reward: 800,
        status: 'upcoming',
        progress: 0,
        deadline: '1 week left',
        featured: false
      },
      {
        title: 'Urban Planning Innovation',
        subtitle: 'Design smart city layouts for megacities worldwide',
        category: 'Urban Planning',
        location: 'Tokyo, Japan',
        difficulty: 'Intermediate',
        duration: '3 weeks',
        participants: 95,
        reward: 700,
        status: 'active',
        progress: 20,
        deadline: '2 weeks left',
        featured: false
      }
    ],
    university: [
      {
        title: 'Campus Sustainability Initiative',
        subtitle: 'Make your campus more environmentally friendly',
        category: 'Sustainability',
        university: 'King Saud University, Saudi Arabia',
        difficulty: 'Intermediate',
        duration: '2 weeks',
        participants: 42,
        reward: 400,
        status: 'active',
        progress: 75,
        deadline: '3 days left',
        featured: true
      },
      {
        title: 'Student Housing Innovation',
        subtitle: 'Redesign student living spaces',
        category: 'Architecture',
        university: 'University of Bahrain, Bahrain',
        difficulty: 'Beginner',
        duration: '1 week',
        participants: 28,
        reward: 300,
        status: 'active',
        progress: 30,
        deadline: '4 days left',
        featured: false
      },
      {
        title: 'Engineering Lab Renovation',
        subtitle: 'Modernize mechanical and civil engineering laboratories',
        category: 'Engineering',
        university: 'American University of Sharjah, UAE',
        difficulty: 'Intermediate',
        duration: '2 weeks',
        participants: 32,
        reward: 550,
        status: 'active',
        progress: 35,
        deadline: '1 week left',
        featured: false
      },
      {
        title: 'Architecture Studio Design',
        subtitle: 'Create collaborative workspaces for architecture students',
        category: 'Architecture',
        university: 'King Fahd University of Petroleum & Minerals, Saudi Arabia',
        difficulty: 'Advanced',
        duration: '3 weeks',
        participants: 28,
        reward: 700,
        status: 'upcoming',
        progress: 0,
        deadline: '2 days left',
        featured: false
      },
      {
        title: 'Structural Testing Facility',
        subtitle: 'Design advanced materials testing laboratory',
        category: 'Engineering',
        university: 'United Arab Emirates University, UAE',
        difficulty: 'Advanced',
        duration: '4 weeks',
        participants: 24,
        reward: 800,
        status: 'active',
        progress: 20,
        deadline: '3 weeks left',
        featured: false
      },
      {
        title: 'Campus Green Building',
        subtitle: 'Design LEED-certified sustainable campus building',
        category: 'Architecture',
        university: 'Bahrain Polytechnic, Bahrain',
        difficulty: 'Intermediate',
        duration: '3 weeks',
        participants: 36,
        reward: 600,
        status: 'completed',
        progress: 100,
        deadline: 'Completed',
        featured: false
      },
      {
        title: 'Digital Fabrication Lab',
        subtitle: 'Create advanced manufacturing and 3D printing facility',
        category: 'Engineering',
        university: 'Zayed University, UAE',
        difficulty: 'Beginner',
        duration: '2 weeks',
        participants: 44,
        reward: 400,
        status: 'active',
        progress: 60,
        deadline: '1 week left',
        featured: false
      }
    ]
  };

  // const getDifficultyColor = (difficulty) => {
  //   switch (difficulty) {
  //     case 'Beginner': return 'bg-green-100 text-green-700';
  //     case 'Intermediate': return 'bg-amber-100 text-amber-700';
  //     case 'Advanced': return 'bg-red-100 text-red-700';
  //     default: return 'bg-gray-100 text-gray-700';
  //   }
  // };

  // const getStatusColor = (status) => {
  //   switch (status) {
  //     case 'active': return 'bg-blue-100 text-blue-700';
  //     case 'completed': return 'bg-green-100 text-green-700';
  //     default: return 'bg-gray-100 text-gray-700';
  //   }
  // };

  const handleNavigationClick = (item) => {
    if (item.isLogout) {
      navigate(item.path);
    } else {
      navigate(item.path);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const menuItems = [
    { label: 'HOME', path: '/app' },
    { label: 'STUDY', path: '/app/study' },
    { label: 'CHALLENGES', path: '/app/challenges' },
    { label: 'COMMUNITY', path: '/app/community' },
    { label: 'PIN UP', path: '/app/pinup' },
    { label: 'PROGRESS', path: '/app/progress' },
    { label: 'PROFILE', path: '/app/profile' },
    { label: 'LOG OUT', path: '/login', isLogout: true }
  ];

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Sidebar Navigation */}
      <div className="fixed top-0 left-0 h-screen z-40">
        <NavigationCarousel
          menuItems={menuItems}
          onItemClick={handleNavigationClick}
          isCollapsed={isSidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
      </div>

      {/* Main Content - Adjusted for Sidebar */}
      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'ml-20' : 'ml-56'}`}>
        {/* Dashboard Header */}
        <DashboardHeader title="CHALLENGES" isSidebarCollapsed={isSidebarCollapsed} />

        {/* Search Section */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className="max-w-7xl px-6 py-4" style={{ 
            marginLeft: isSidebarCollapsed ? '20px' : '20px',
            transition: 'margin-left 0.3s ease'
          }}>
                <SearchBar 
              placeholder="Search local, international, university challenges..." 
                  onSearch={handleSearch}
                />
          </div>
        </div>
      
      {/* Tab Navigation - Switch Tab */}
      <div className={`${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} border-t`}>
        <div className="max-w-6xl px-6" style={{ 
          marginLeft: isSidebarCollapsed ? '20px' : '20px',
          transition: 'margin-left 0.3s ease'
        }}>
        <div className="flex justify-between gap-6 flex-wrap">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
              className={`relative flex-1 min-w-[140px] px-6 py-6 font-bold text-sm uppercase tracking-wider transition-all duration-300 text-center ${
                  activeTab === tab.id
                    ? 'text-[#AC5757]'
                    : isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <span className="relative z-10">{tab.label}</span>
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#AC5757] rounded-t-lg"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

        <div className="max-w-7xl px-6 py-8" style={{ 
          marginLeft: isSidebarCollapsed ? '20px' : '20px',
          transition: 'margin-left 0.3s ease'
        }}>

        {/* Challenge Sections */}
        <div className="space-y-8">
          {/* SELECTED Section */}
          <section>
            <h2 className={`font-judson text-3xl font-bold mb-6 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>SELECTED</h2>
            <div className="space-y-4">
              {challenges[activeTab].filter(c => c.featured).map((challenge, idx) => {
                const isCompleted = challenge.status === 'completed' || challenge.progress === 100;
                const isUpcoming = challenge.status === 'upcoming';
                const statusLabel = isCompleted ? 'COMPLETED' : isUpcoming ? 'UPCOMING' : 'ACTIVE';
                const statusClass = isCompleted
                  ? 'bg-green-100 text-green-800'
                  : isUpcoming
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-blue-100 text-blue-800';

                return (
                <div 
                  key={idx} 
                      className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-xl p-5 md:p-6 grid gap-6 md:grid-cols-[minmax(0,1fr)_auto] items-center hover:shadow-lg transition-shadow cursor-pointer overflow-hidden`}
                  onClick={() => handleChallengeClick(challenge)}
                >
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                          <h3 className={`font-semibold text-lg md:text-xl leading-snug ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {challenge.title}
                          </h3>
                      {challenge.title === 'Green Tech Park Design' && (
                            <div className="px-2 py-[2px] bg-purple-100 rounded-full flex items-center justify-center">
                              <span className="text-purple-600 text-[10px] font-bold tracking-wide">AI</span>
                        </div>
                      )}
                    </div>
                        <span className={`px-2 py-1 rounded-full font-medium text-xs md:text-sm ${statusClass}`}>
                          {statusLabel}
                        </span>
                      </div>
                      <p className={`text-sm md:text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{challenge.subtitle || challenge.category}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm">
                        <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{challenge.category}</span>
                    {challenge.university && (
                          <span className={`font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>{challenge.university}</span>
                    )}
                    {challenge.location && (
                          <span className={`font-medium ${isDarkMode ? 'text-green-300' : 'text-green-600'}`}>{challenge.location}</span>
                    )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm">
                        <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Deadline: {challenge.deadline}</span>
                        <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Participants: {challenge.participants}</span>
                        <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Duration: {challenge.duration}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap md:flex-nowrap justify-start md:justify-end items-center gap-6 md:gap-8 w-full">
                      <div className="text-center min-w-[70px]">
                        <p className={`text-[11px] uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Progress</p>
                        <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{challenge.progress}%</p>
                      </div>
                      <div className="text-center min-w-[90px]">
                        <p className={`text-[11px] uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Reward</p>
                        <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>+{challenge.reward} XP</p>
                  </div>
                      <div className="text-center min-w-[90px]">
                        <p className={`text-[11px] uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Difficulty</p>
                        <p className="text-lg font-semibold text-blue-600">{challenge.difficulty}</p>
                    </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* AI-SUGGESTED Section */}
          <section>
            <h2 className={`font-judson text-3xl font-bold mb-6 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>AI-SUGGESTED</h2>
            <div className="space-y-4">
              {challenges[activeTab].filter(c => !c.featured).slice(0, 2).map((challenge, idx) => {
                const isCompleted = challenge.status === 'completed' || challenge.progress === 100;
                const isUpcoming = challenge.status === 'upcoming';
                const statusLabel = isCompleted ? 'COMPLETED' : isUpcoming ? 'UPCOMING' : 'ACTIVE';
                const statusClass = isCompleted
                  ? 'bg-green-100 text-green-800'
                  : isUpcoming
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-blue-100 text-blue-800';

                const showAIBadge = ['Smart City Infrastructure', 'Community Center Renovation', 'Global Climate Solutions', 'Campus Sustainability Initiative', 'Structural Engineering Analysis', 'Sustainable Architecture Design', 'International Bridge Design', 'Environmental Engineering Solutions'].includes(challenge.title);

                return (
                <div 
                  key={idx} 
                      className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-5 md:p-6 grid gap-6 md:grid-cols-[minmax(0,1fr)_auto] items-center hover:shadow-lg transition-shadow cursor-pointer overflow-hidden`}
                  onClick={() => handleChallengeClick(challenge)}
                >
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                          <h3 className={`font-semibold text-lg md:text-xl leading-snug ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{challenge.title}</h3>
                          {showAIBadge && (
                            <div className="px-2 py-[2px] bg-purple-100 rounded-full flex items-center justify-center">
                              <span className="text-purple-600 text-[10px] font-bold tracking-wide">AI</span>
                        </div>
                      )}
                    </div>
                        <span className={`px-2 py-1 rounded-full font-medium text-xs md:text-sm ${statusClass}`}>
                          {statusLabel}
                      </span>
                    </div>
                      <p className={`text-sm md:text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{challenge.subtitle || challenge.category}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm">
                        <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Deadline: {challenge.deadline}</span>
                        <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Category: {challenge.category}</span>
                  </div>
                    </div>
                    <div className="flex flex-wrap md:flex-nowrap justify-start md:justify-end items-center gap-6 md:gap-8 w-full">
                      <div className="text-center min-w-[70px]">
                        <p className={`text-[11px] uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Progress</p>
                        <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{challenge.progress}%</p>
                      </div>
                      <div className="text-center min-w-[90px]">
                        <p className={`text-[11px] uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Reward</p>
                        <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>+{challenge.reward} XP</p>
                      </div>
                      <div className="text-center min-w-[90px]">
                        <p className={`text-[11px] uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Difficulty</p>
                        <p className={`text-lg font-semibold ${
                        challenge.difficulty === 'Beginner' ? 'text-orange-600' :
                        challenge.difficulty === 'Intermediate' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {challenge.difficulty}
                      </p>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </section>

          {/* Additional Section */}
          {challenges[activeTab].filter(c => !c.featured).slice(2).length > 0 && (
            <section>
              <div className="space-y-4">
                {challenges[activeTab].filter(c => !c.featured).slice(2).map((challenge, idx) => {
                  const isCompleted = challenge.status === 'completed' || challenge.progress === 100;
                  const isUpcoming = challenge.status === 'upcoming';
                  const statusLabel = isCompleted ? 'COMPLETED' : isUpcoming ? 'UPCOMING' : 'ACTIVE';
                  const statusClass = isCompleted
                    ? 'bg-green-100 text-green-800'
                    : isUpcoming
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-blue-100 text-blue-800';

                  const showAIBadge = ['Smart City Infrastructure', 'Community Center Renovation', 'Global Climate Solutions', 'Campus Sustainability Initiative', 'Structural Engineering Analysis', 'Sustainable Architecture Design', 'International Bridge Design', 'Environmental Engineering Solutions'].includes(challenge.title);

                  return (
                  <div 
                    key={idx} 
                        className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-5 md:p-6 grid gap-6 md:grid-cols-[minmax(0,1fr)_auto] items-center hover:shadow-lg transition-shadow cursor-pointer overflow-hidden`}
                    onClick={() => handleChallengeClick(challenge)}
                  >
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                            <h3 className={`font-semibold text-lg md:text-xl leading-snug ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{challenge.title}</h3>
                            {showAIBadge && (
                              <div className="px-2 py-[2px] bg-purple-100 rounded-full flex items-center justify-center">
                                <span className="text-purple-600 text-[10px] font-bold tracking-wide">AI</span>
                          </div>
                        )}
                      </div>
                          <span className={`px-2 py-1 rounded-full font-medium text-xs md:text-sm ${statusClass}`}>
                            {statusLabel}
                          </span>
                        </div>
                        <p className={`text-sm md:text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{challenge.subtitle || challenge.category}</p>
                        <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm">
                      {challenge.university && (
                            <span className={`font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>{challenge.university}</span>
                      )}
                      {challenge.location && (
                            <span className={`font-medium ${isDarkMode ? 'text-green-300' : 'text-green-600'}`}>{challenge.location}</span>
                      )}
                          <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Deadline: {challenge.deadline}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap md:flex-nowrap justify-start md:justify-end items-center gap-6 md:gap-8 w-full">
                        <div className="text-center min-w-[70px]">
                          <p className={`text-[11px] uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Progress</p>
                          <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{challenge.progress}%</p>
                    </div>
                        <div className="text-center min-w-[90px]">
                          <p className={`text-[11px] uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Reward</p>
                          <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>+{challenge.reward} XP</p>
                      </div>
                        <div className="text-center min-w-[90px]">
                          <p className={`text-[11px] uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Difficulty</p>
                          <p className={`text-lg font-semibold ${
                          challenge.difficulty === 'Beginner' ? 'text-orange-600' :
                          challenge.difficulty === 'Intermediate' ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {challenge.difficulty}
                        </p>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Challenge Detail Modal */}
      {showChallengeModal && selectedChallenge && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className={`sticky top-0 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b p-6 rounded-t-2xl`}>
              <div className="flex justify-between items-start">
                <div className="flex-1 mr-4">
                  <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedChallenge.title}</h2>
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>{selectedChallenge.subtitle}</p>
                </div>
                <button 
                  onClick={() => setShowChallengeModal(false)}
                  className={isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy size={20} className="text-[#AC5757]" />
                    <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Challenge Details</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Category:</span>
                      <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedChallenge.category}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Difficulty:</span>
                      <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedChallenge.difficulty}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Duration:</span>
                      <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedChallenge.duration}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Deadline:</span>
                      <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedChallenge.deadline}</span>
                    </div>
                  </div>
                </div>
                
                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Users size={20} className="text-[#AC5757]" />
                    <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Community</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Participants:</span>
                      <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedChallenge.participants}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Your Progress:</span>
                      <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedChallenge.progress}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Reward:</span>
                      <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedChallenge.reward} XP</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Status:</span>
                      <span className={`font-medium capitalize ${selectedChallenge.status === 'active' ? 'text-blue-600' : 'text-green-600'}`}>
                        {selectedChallenge.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Challenge Brief</h3>
                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
                  <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {selectedChallenge.subtitle} This challenge will test your skills in {selectedChallenge.category.toLowerCase()} and provide hands-on experience with real-world scenarios.
                  </p>
                  <div className="space-y-2">
                    <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>What you'll do:</h4>
                    <ul className={`list-disc list-inside text-sm space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <li>Research and analyze the problem context</li>
                      <li>Develop innovative design solutions</li>
                      <li>Create detailed presentations and prototypes</li>
                      <li>Collaborate with team members</li>
                      <li>Present your final solution to judges</li>
                    </ul>
                    
                    {(selectedChallenge.title === 'Green Tech Park Design' || 
                      selectedChallenge.title === 'Smart City Infrastructure' || 
                      selectedChallenge.title === 'Community Center Renovation' ||
                      selectedChallenge.title === 'Structural Engineering Analysis' ||
                      selectedChallenge.title === 'Sustainable Architecture Design' ||
                      selectedChallenge.title === 'International Bridge Design' ||
                      selectedChallenge.title === 'Environmental Engineering Solutions') && (
                      <div className={`mt-4 p-3 ${isDarkMode ? 'bg-purple-900/30 border-purple-700' : 'bg-purple-50 border-purple-200'} rounded-lg border`}>
                        <h4 className={`font-medium mb-2 flex items-center gap-2 ${isDarkMode ? 'text-purple-200' : 'text-purple-900'}`}>
                          <div className={`w-4 h-4 ${isDarkMode ? 'bg-purple-700' : 'bg-purple-100'} rounded-full flex items-center justify-center`}>
                            <span className={`text-xs font-bold ${isDarkMode ? 'text-purple-200' : 'text-purple-600'}`}>AI</span>
                          </div>
                          Start with AI
                        </h4>
                        <p className={`text-sm ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                          This challenge includes AI-powered tools to help you get started. Use our AI assistant to generate initial concepts, analyze requirements, and explore design possibilities before diving into your solution.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                {selectedChallenge.status === 'active' && selectedChallenge.progress > 0 ? (
                  <button 
                    onClick={() => handleStartChallenge(selectedChallenge)}
                    className="flex-1 bg-[#AC5757] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#8A4A4A] transition-colors"
                  >
                    Continue Challenge
                  </button>
                ) : selectedChallenge.status === 'completed' ? (
                  <button 
                    disabled
                    className={`flex-1 ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-300 text-gray-500'} py-3 px-6 rounded-lg font-semibold cursor-not-allowed`}
                  >
                    Challenge Completed
                  </button>
                ) : (
                  <button 
                    onClick={() => handleJoinChallenge(selectedChallenge)}
                    className="flex-1 bg-[#AC5757] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#8A4A4A] transition-colors"
                  >
                    Join Challenge
                  </button>
                )}
                <button 
                  onClick={() => setShowChallengeModal(false)}
                  className={`px-6 py-3 border rounded-lg transition-colors ${
                    isDarkMode
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

export default Challenges;